import * as React from "react";
import { Modal, Button, Table, Icon, Header } from "semantic-ui-react";
import { AIAgentTask } from "../../../../../generated/api";
import ReactTable from "react-table";

interface ModelPricing {
    model: AIAgentTask.AiModelEnumEnum;
    displayName: string;
    inputPrice: number; // price per 1M input tokens
    outputPrice: number; // price per 1M output tokens
    maxInput: number; // max input tokens
    maxOutput: number; // max output tokens
    thinking: string
}

enum ThinkingMode {
    DEFAULT = "default",
    NONE = "none",
    OPTIONAL = "optional",
}

interface ModelPricingTableProps {
    isOpen: boolean;
    onClose: () => void;
}

export function ModelPricingTable(props: ModelPricingTableProps) {
    const [sorted, setSorted] = React.useState([{ id: 'model', desc: false }]);

    const modelPricingData: ModelPricing[] = [
        {
            model: AIAgentTask.AiModelEnumEnum.DeepSeekV3,
            displayName: "DeepSeek-V3-0324",
            inputPrice: 0.27,
            outputPrice: 1.1,
            maxInput: 64000,
            maxOutput: 8000,
            thinking: ThinkingMode.OPTIONAL,

        },
        {
            model: AIAgentTask.AiModelEnumEnum.GEMINI25PRO,
            displayName: "gemini-2.5-pro-preview-03-25",
            inputPrice: 1.25,
            outputPrice: 10,
            maxInput: 1048576,
            maxOutput: 65536,
            thinking: ThinkingMode.DEFAULT,

        },
        {
            model: AIAgentTask.AiModelEnumEnum.GEMINI25FLASH,
            displayName: "gemini-2.5-flash-preview-04-17",
            inputPrice:0.6,
            outputPrice: 0.15,
            maxInput: 1048576,
            maxOutput: 65536,
            thinking: ThinkingMode.OPTIONAL,

        },
        {
            model: AIAgentTask.AiModelEnumEnum.GPT4O,
            displayName: "gpt-4o-2024-08-06",
            inputPrice: 2.5,
            outputPrice: 10.0,
            maxInput: 128000,
            maxOutput: 16384,
            thinking: ThinkingMode.NONE,

        },
        {
            model: AIAgentTask.AiModelEnumEnum.Claude45Haiku,
            displayName: "claude-haiku-4-5-20251001",
            inputPrice: 1,
            outputPrice: 5.0,
            maxInput: 200000,
            maxOutput: 64000,
            thinking: ThinkingMode.OPTIONAL,

        },
        {
            model: AIAgentTask.AiModelEnumEnum.GPT4OMini,
            displayName: "gpt-4o-mini-2024-07-18",
            inputPrice: 0.15,
            outputPrice: 0.6,
            maxInput: 128000,
            maxOutput: 16384,
            thinking: ThinkingMode.NONE,

        },
        {
            model: AIAgentTask.AiModelEnumEnum.Claude4Sonnet,
            displayName: "claude-sonnet-4-20250514",
            inputPrice: 3.0,
            outputPrice: 15.0,
            maxInput: 200000,
            maxOutput: 64000,
            thinking: ThinkingMode.OPTIONAL,

        },
        {
            model: AIAgentTask.AiModelEnumEnum.Claude37Sonnet,
            displayName: "claude-3-7-sonnet-20250219",
            inputPrice: 3.0,
            outputPrice: 15.0,
            maxInput: 200000,
            maxOutput: 64000,
            thinking: ThinkingMode.NONE,

        },
        {
            model: AIAgentTask.AiModelEnumEnum.Claude35Sonnet,
            displayName: "claude-3-5-sonnet-20241022",
            inputPrice: 3.0,
            outputPrice: 15.0,
            maxInput: 200000,
            maxOutput: 8192,
            thinking: ThinkingMode.NONE,

        },
        {
            model: AIAgentTask.AiModelEnumEnum.GPT4TURBO,
            displayName: "gpt-4-turbo-2024-04-09",
            inputPrice: 10.0,
            outputPrice: 30.0,
            maxInput: 128000,
            maxOutput: 4096,
            thinking: ThinkingMode.NONE,

        },
        {
            model: AIAgentTask.AiModelEnumEnum.GPT35,
            displayName: "gpt-3.5-turbo-0125",
            inputPrice: 0.5,
            outputPrice: 1.5,
            maxInput: 16385,
            maxOutput: 4096,
            thinking: ThinkingMode.NONE,

        },
    ];

    const columns = [
        {
            Header: "Model",
            accessor: "displayName",
            width: 150
        },
        {
            Header: "Input Price (per 1M tokens)",
            accessor: "inputPrice",
            Cell: (row) => `$${row.value.toFixed(2)}`,
            width: 182
        },
        {
            Header: "Output Price (per 1M tokens)",
            accessor: "outputPrice",
            Cell: (row) => `$${row.value.toFixed(2)}`,
            width: 190
        },
        {
            Header: "Max Input (tokens)",
            accessor: "maxInput",
            Cell: (row) => row.value.toLocaleString(),
            width: 140
        },
        {
            Header: "Max Output (tokens)",
            accessor: "maxOutput",
            Cell: (row) => row.value.toLocaleString(),
            width: 140
        },
        {
            Header: "Thinking Mode",
            accessor: "thinking",
            Cell: (row) => row.value.toLocaleString(),
            width: 105
        }
    ];

    return (
        <Modal
            open={props.isOpen}
            onClose={props.onClose}
            style={{
                margin: 'auto',
                overflow: 'hidden',
            }}
        >
            <Modal.Header>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <Icon name="dollar sign" style={{ marginRight: "10px" }} />
                    AI Models API Pricing Information
                </div>
            </Modal.Header>
            <Modal.Content>
                <ReactTable
                    data={modelPricingData}
                    columns={columns}
                    defaultPageSize={modelPricingData.length}
                    showPagination={false}
                    className="-striped -highlight"
                    sorted={sorted}
                    onSortedChange={newSorted => setSorted(newSorted)}
                    minRows={1}
                    style={{ marginTop: "10px" }}
                />
                <div style={{ marginTop: "15px", fontSize: "0.9em", color: "#666" }}>
                    <p><Icon name="info circle" /> Note: API prices might change especially with OPENAI</p>
                </div>
            </Modal.Content>
            <Modal.Actions>
                <Button color="blue" onClick={props.onClose}>
                    Close
                </Button>
            </Modal.Actions>
        </Modal>
    );
}
